// Spec's `present` layouts: the worker checks the model's tree; its replay and the web read saved ones.
import { z } from 'zod';

export const SPEC_PRESENT_RESULT_TYPE = 'spec-present';

export const SPEC_PRESENT_LIMITS = {
  maxDepth: 4,
  maxNodes: 40,
  /** Measured as sent: the tree's JSON as a string inside the event's JSON (API cap 30,000). */
  maxBytes: 24_576,
  maxString: 2_000,
  tableColumns: 8,
  tableRows: 50,
  chartSeries: 4,
  chartPoints: 60,
  recordsIds: 100,
  mapIds: 25,
  callsPerTurn: 3,
} as const;

export const SPEC_PRESENT_ENTITIES = ['projects', 'accounts', 'contacts'] as const;
export type SpecPresentEntity = (typeof SPEC_PRESENT_ENTITIES)[number];

/** A search's handle in one turn (`r1`, `r2`, ...), told to the model with the search's result. */
export const SPEC_PRESENT_REF = /^r[1-9]\d{0,3}$/;

export type SpecPresentLeaf =
  | { type: 'Header'; text: string }
  | { type: 'Text'; markdown: string }
  | { type: 'Fact'; label: string; value: string; hint?: string }
  | { type: 'Table'; columns: string[]; rows: string[][] }
  | {
      type: 'Chart';
      kind: 'bar' | 'line' | 'area';
      x: string[];
      series: { name: string; values: (number | null)[] }[];
    }
  | { type: 'Alert'; tone: 'info' | 'warning'; text: string }
  | { type: 'RecordCard'; entity: SpecPresentEntity; id: string };

/** A saved block: `Records` and `Map` hold resolved ids, with the search's own counts when it ranked. */
export type SpecPresentBlock =
  | SpecPresentLeaf
  | { type: 'Card'; title?: string; children: SpecPresentBlock[] }
  | { type: 'Row'; children: SpecPresentBlock[] }
  | {
      type: 'Records';
      entity: SpecPresentEntity;
      ids: string[];
      counts?: number[];
      countLabel?: string;
      /** The query column `counts` came from, when Spec named one: it sits beside the stored total. */
      countColumn?: string;
      title?: string;
    }
  | { type: 'Map'; projectIds: string[] };

/** What the model sends: `Records` and `Map` may name a search instead of ids. */
export type SpecPresentInputBlock =
  | SpecPresentLeaf
  | { type: 'Card'; title?: string; children: SpecPresentInputBlock[] }
  | { type: 'Row'; children: SpecPresentInputBlock[] }
  | {
      type: 'Records';
      from?: string;
      /** With `from`: the search's first rows only. */
      limit?: number;
      entity?: SpecPresentEntity;
      ids?: string[];
      /** With `from`: the search's number column to show beside each record. */
      count?: string;
      countLabel?: string;
      title?: string;
    }
  | { type: 'Map'; from?: string; limit?: number; projectIds?: string[] };

export interface SpecPresentResult {
  type: typeof SPEC_PRESENT_RESULT_TYPE;
  blocks: SpecPresentBlock[];
}

const L = SPEC_PRESENT_LIMITS;
const BLOCK_TYPES = [
  'Header',
  'Text',
  'Fact',
  'Card',
  'Row',
  'Table',
  'Chart',
  'Alert',
  'RecordCard',
  'Records',
  'Map',
] as const;

const text = z.string().max(L.maxString, `at most ${L.maxString} characters`);
// The member reads cells and facts as text; the model sends numbers too.
const cell = z
  .union([text, z.number().finite(), z.null()])
  .transform((value) => (value === null ? '' : String(value)));
const recordId = z.string().uuid('a record id (uuid)');
const entity = z.preprocess(
  (value) =>
    value === 'project' || value === 'account' || value === 'contact' ? `${value}s` : value,
  z.enum(SPEC_PRESENT_ENTITIES),
);
const ref = z.string().regex(SPEC_PRESENT_REF, 'from is a search ref like r2');
const ids = (max: number) =>
  z.array(recordId).min(1, 'at least one id').max(max, `at most ${max} ids`);
const limit = (max: number) =>
  z.number().int().min(1).max(max, `limit is at most ${max}`).optional();
const UUID_CELL = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LEAVES = {
  Header: z.object({ type: z.literal('Header'), text }),
  Text: z.object({ type: z.literal('Text'), markdown: text }),
  Fact: z.object({ type: z.literal('Fact'), label: text, value: cell, hint: text.optional() }),
  Table: z
    .object({
      type: z.literal('Table'),
      columns: z.array(text).min(1).max(L.tableColumns, `at most ${L.tableColumns} columns`),
      rows: z.array(z.array(cell)).max(L.tableRows, `at most ${L.tableRows} rows`),
    })
    .superRefine((table, ctx) => {
      table.rows.forEach((row, index) => {
        if (row.length > table.columns.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rows', index],
            message: `a row has at most one cell per column (${table.columns.length})`,
          });
        }
      });
    })
    .transform((table) => ({
      ...table,
      rows: table.rows.map((row) => [
        ...row,
        ...Array<string>(table.columns.length - row.length).fill(''),
      ]),
    })),
  Chart: z
    .object({
      type: z.literal('Chart'),
      kind: z.enum(['bar', 'line', 'area']),
      x: z
        .array(z.union([text, z.number().finite()]).transform(String))
        .min(1)
        .max(L.chartPoints, `at most ${L.chartPoints} points`),
      series: z
        .array(z.object({ name: text, values: z.array(z.number().finite().nullable()) }))
        .min(1)
        .max(L.chartSeries, `at most ${L.chartSeries} series`),
    })
    .superRefine((chart, ctx) => {
      chart.series.forEach((series, index) => {
        if (series.values.length !== chart.x.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['series', index, 'values'],
            message: `needs one value per x label (${chart.x.length})`,
          });
        }
      });
    }),
  Alert: z.object({ type: z.literal('Alert'), tone: z.enum(['info', 'warning']), text }),
  RecordCard: z.object({ type: z.literal('RecordCard'), entity, id: recordId }),
} as const;

/** A plain lower-case column name: what Records' `count` may name, and all a ref lists. */
export const SPEC_PRESENT_COUNT_COLUMN = /^(?!__)[a-z_][a-z0-9_]{0,62}$/;

const INPUT_REFS = {
  Records: z
    .object({
      type: z.literal('Records'),
      from: ref.optional(),
      limit: limit(L.recordsIds),
      entity: entity.optional(),
      ids: ids(L.recordsIds).optional(),
      count: z
        .string()
        .regex(SPEC_PRESENT_COUNT_COLUMN, 'count names one column of the search')
        .optional(),
      countLabel: text.optional(),
      title: text.optional(),
    })
    .superRefine((block, ctx) => {
      if ((block.from === undefined) === (block.ids === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Records needs either from or ids (not both)',
        });
      } else if (block.limit !== undefined && block.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['limit'],
          message: 'limit goes with from',
        });
      } else if (block.count !== undefined && block.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['count'],
          message: 'count goes with from',
        });
      } else if (block.ids !== undefined && block.entity === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entity'],
          message: 'entity is required with ids',
        });
      }
    }),
  Map: z
    .object({
      type: z.literal('Map'),
      from: ref.optional(),
      limit: limit(L.mapIds),
      projectIds: ids(L.mapIds).optional(),
    })
    .superRefine((block, ctx) => {
      if ((block.from === undefined) === (block.projectIds === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Map needs either from or projectIds (not both)',
        });
      } else if (block.limit !== undefined && block.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['limit'],
          message: 'limit goes with from',
        });
      }
    }),
} as const;

const SAVED_REFS = {
  Records: z
    .object({
      type: z.literal('Records'),
      entity: z.enum(SPEC_PRESENT_ENTITIES),
      ids: ids(L.recordsIds),
      counts: z.array(z.number().finite()).optional(),
      countLabel: text.optional(),
      countColumn: z.string().regex(SPEC_PRESENT_COUNT_COLUMN).optional(),
      title: text.optional(),
    })
    .transform(({ counts, ...block }) =>
      counts && counts.length === block.ids.length ? { ...block, counts } : block,
    ),
  Map: z.object({ type: z.literal('Map'), projectIds: ids(L.mapIds) }),
} as const;

const CONTAINER_HEAD = z.object({ title: text.optional() });

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

class PresentInputError extends Error {}

function fail(message: string): never {
  throw new PresentInputError(message);
}

function issuePath(base: string, path: readonly (string | number)[]): string {
  return path.reduce<string>(
    (out, part) => (typeof part === 'number' ? `${out}[${part}]` : `${out}.${part}`),
    base,
  );
}

function parsed(result: z.SafeParseReturnType<unknown, unknown>, path: string): unknown {
  if (result.success) return result.data;
  const issue = result.error.issues[0];
  return fail(`${issuePath(path, issue?.path ?? [])}: ${issue?.message ?? 'invalid'}`);
}

// Models mix up a block's text field; read the other one rather than spend a step on a refusal.
const TEXT_ALIASES: Readonly<Record<string, readonly [from: string, to: string]>> = {
  Text: ['text', 'markdown'],
  Header: ['markdown', 'text'],
  Alert: ['markdown', 'text'],
};

function withTextAlias(raw: Record<string, unknown>, type: string): Record<string, unknown> {
  const alias = TEXT_ALIASES[type];
  if (!alias || raw[alias[1]] !== undefined || typeof raw[alias[0]] !== 'string') return raw;
  return { ...raw, [alias[1]]: raw[alias[0]] };
}

function inputBlock(
  raw: unknown,
  path: string,
  depth: number,
  count: { nodes: number },
): SpecPresentInputBlock {
  if (depth > L.maxDepth) fail(`${path}: layouts nest no deeper than ${L.maxDepth}`);
  count.nodes += 1;
  if (count.nodes > L.maxNodes) fail(`a layout has at most ${L.maxNodes} blocks`);
  if (!isRecord(raw)) fail(`${path}: each block is an object with a type`);
  const type = raw.type;
  if (type === 'Card' || type === 'Row') {
    const head = parsed(CONTAINER_HEAD.safeParse(raw), path) as { title?: string };
    if (!Array.isArray(raw.children) || raw.children.length === 0) {
      fail(`${path}.children: a list of blocks`);
    }
    const children = raw.children.map((child, index) =>
      inputBlock(child, `${path}.children[${index}]`, depth + 1, count),
    );
    return type === 'Card'
      ? { type, ...(head.title === undefined ? {} : { title: head.title }), children }
      : { type, children };
  }
  if (type === 'Records' || type === 'Map') {
    return parsed(INPUT_REFS[type].safeParse(raw), path) as SpecPresentInputBlock;
  }
  if (typeof type === 'string' && type in LEAVES) {
    const block = parsed(
      LEAVES[type as keyof typeof LEAVES].safeParse(withTextAlias(raw, type)),
      path,
    ) as SpecPresentInputBlock;
    if (block.type === 'Table') refuseRecordIds(block, path);
    return block;
  }
  return fail(`${path}.type: one of ${BLOCK_TYPES.join(', ')}`);
}

// New tables only, so saved ones still read: a record id belongs in Records, whose rows open it.
function refuseRecordIds(table: Extract<SpecPresentLeaf, { type: 'Table' }>, path: string): void {
  table.rows.forEach((row, index) => {
    const cell = row.findIndex((value) => UUID_CELL.test(value.trim()));
    if (cell >= 0) {
      fail(
        `${path}.rows[${index}][${cell}]: a record id. Show records with Records ({"type":"Records","from":"r1","limit":10}), whose rows open the record; never print ids in a Table`,
      );
    }
  });
}

/** Strict: the model's call is refused with the first problem, named by path. */
export function parseSpecPresentInput(
  raw: unknown,
): { ok: true; blocks: SpecPresentInputBlock[] } | { ok: false; message: string } {
  const blocks = isRecord(raw) ? raw.blocks : undefined;
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { ok: false, message: `blocks: a list of 1 to ${L.maxNodes} blocks` };
  }
  try {
    const count = { nodes: 0 };
    return {
      ok: true,
      blocks: blocks.map((block, index) => inputBlock(block, `blocks[${index}]`, 1, count)),
    };
  } catch (error) {
    if (error instanceof PresentInputError) return { ok: false, message: error.message };
    throw error;
  }
}

export function measureSpecPresent(blocks: readonly (SpecPresentBlock | SpecPresentInputBlock)[]): {
  nodes: number;
  depth: number;
} {
  let nodes = 0;
  let depth = 0;
  const walk = (list: readonly (SpecPresentBlock | SpecPresentInputBlock)[], level: number) => {
    for (const block of list) {
      nodes += 1;
      depth = Math.max(depth, level);
      if (block.type === 'Card' || block.type === 'Row') walk(block.children, level + 1);
    }
  };
  walk(blocks, 1);
  return { nodes, depth };
}

/** Records of projects has its own map view; a Map of projects it already lists would draw the same pins twice. */
export function withoutRepeatedMaps(blocks: SpecPresentBlock[]): SpecPresentBlock[] {
  const listed = new Set<string>();
  const collect = (list: readonly SpecPresentBlock[]): void => {
    for (const block of list) {
      if (block.type === 'Records' && block.entity === 'projects') {
        for (const id of block.ids) listed.add(id.toLowerCase());
      }
      if (block.type === 'Card' || block.type === 'Row') collect(block.children);
    }
  };
  collect(blocks);
  if (listed.size === 0) return blocks;
  const keep = (list: readonly SpecPresentBlock[]): SpecPresentBlock[] =>
    list.flatMap((block): SpecPresentBlock[] => {
      if (block.type === 'Map') {
        return block.projectIds.every((id) => listed.has(id.toLowerCase())) ? [] : [block];
      }
      if (block.type === 'Card' || block.type === 'Row') {
        const children = keep(block.children);
        return children.length > 0 ? [{ ...block, children }] : [];
      }
      return [block];
    });
  return keep(blocks);
}

/** The `presentJson` a `tool_call` event carries. */
export function serializeSpecPresent(blocks: readonly SpecPresentBlock[]): string {
  return JSON.stringify({ type: SPEC_PRESENT_RESULT_TYPE, blocks });
}

/** Bytes `presentJson` costs inside the event's own JSON, escapes included. */
export function specPresentSentBytes(json: string): number {
  return new TextEncoder().encode(JSON.stringify(json)).byteLength;
}

function savedBlock(
  raw: unknown,
  depth: number,
  count: { nodes: number },
): SpecPresentBlock | null {
  if (depth > L.maxDepth || count.nodes >= L.maxNodes || !isRecord(raw)) return null;
  const type = raw.type;
  if (type === 'Card' || type === 'Row') {
    const head = CONTAINER_HEAD.safeParse(raw);
    if (!head.success || !Array.isArray(raw.children)) return null;
    count.nodes += 1;
    const children = raw.children.flatMap((child) => savedBlock(child, depth + 1, count) ?? []);
    if (children.length === 0) return null;
    return type === 'Card'
      ? { type, ...(head.data.title === undefined ? {} : { title: head.data.title }), children }
      : { type, children };
  }
  const schema =
    type === 'Records' || type === 'Map'
      ? SAVED_REFS[type]
      : typeof type === 'string' && type in LEAVES
        ? LEAVES[type as keyof typeof LEAVES]
        : null;
  const result = schema?.safeParse(raw);
  if (!result?.success) return null;
  count.nodes += 1;
  return result.data as SpecPresentBlock;
}

/** Lenient: a saved or streamed layout, skipping any block this build does not know. */
export function readSpecPresent(raw: unknown): SpecPresentResult | null {
  if (!isRecord(raw) || raw.type !== SPEC_PRESENT_RESULT_TYPE || !Array.isArray(raw.blocks)) {
    return null;
  }
  const count = { nodes: 0 };
  const blocks = raw.blocks.flatMap((block) => savedBlock(block, 1, count) ?? []);
  return blocks.length > 0 ? { type: SPEC_PRESENT_RESULT_TYPE, blocks } : null;
}
